/**
 * Shared money-formatting primitive for the cycle count screens (#31279).
 *
 * Renders a numeric amount with grouped thousands separators and exactly two
 * decimal places, e.g. 1234567.5 → "1,234,567.50". It deliberately adds NO
 * currency symbol, sign glyph, or null/placeholder decoration — each call site
 * (web session/report, mobile session/report) keeps its own "$", "+", "−" and
 * em-dash handling and delegates ONLY the grouping here, so one grouping rule
 * stays consistent across every cost/amount field on both platforms.
 *
 * Grouping is done manually (not via toLocaleString options) so the output is
 * deterministic across the vitest (Node/ICU) and NativeScript (V8/JSC) runtimes.
 * A non-finite value (NaN/Infinity) falls back to "0.00" so an upstream bad
 * number never renders as "$NaN" on a counter's screen.
 */
export function formatAmountWithSeparators(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  const negative = value < 0;
  // decPart defaults to '00' so an astronomically large magnitude (>= 1e21,
  // where toFixed switches to exponential and yields no '.') never renders as
  // "...undefined". Real warehouse costs never reach that, but the guard is free.
  const [intPart, decPart = '00'] = Math.abs(value).toFixed(2).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${decPart}`;
}

/**
 * Cost cell display (#31279): a grouped "$1,234.50", with a "−$" prefix for a
 * negative and an em-dash for null/undefined (cost unavailable). Shared so the
 * cycle-count session grid and report render costs identically. Uses the U+2212
 * minus glyph (not ASCII '-') to match the existing report styling.
 */
export function formatCostDisplay(value: number | null | undefined): string {
  if (value == null) return '—';
  const abs = formatAmountWithSeparators(Math.abs(value));
  return value < 0 ? `−$${abs}` : `$${abs}`;
}

/**
 * Signed dollar-impact display (#31279): "+$1,234.50" / "−$1,234.50", and
 * "$0.00" for zero or null (no impact). Shared by the report's $-impact cell and
 * the net-adjustment total, which were previously two identical local formatters.
 */
export function formatImpactDisplay(value: number | null | undefined): string {
  if (value == null || value === 0) return '$0.00';
  const abs = formatAmountWithSeparators(Math.abs(value));
  return value < 0 ? `−$${abs}` : `+$${abs}`;
}

/**
 * #32221: Broken-qty display for the cycle-count variance grid's Broken column.
 * Blank when there is no broken quantity (null/undefined/0) so the column stays
 * clean on the many rows with no breakage — matching the prior inline "Broken: N"
 * which only rendered a positive value. Otherwise the integer count as a string.
 */
export function formatBrokenQty(value: number | null | undefined): string {
  return value ? value.toString() : '';
}
